import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {
  ALL_PAPER_SIZES,
  PAPER_SIZE_INFO,
  PaperSize,
  PrintOrientation,
  PrintPreferences,
} from 'notebook/mediocre/print/PrintConstants';
import {getPageDims, PageDims} from 'notebook/mediocre/print/PrintUtil';
import notifications from 'notifications';
import preferences from 'Preferences';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import {BasicEvent, InputChangeEvent, SelectChangeEvent} from 'util/Types';

interface PrintOptionsModalProps {
  onRequestClose: () => void;
  onRequestPrint: (args: {dimensions: PageDims; shouldFitToWidth: boolean; showLinkURLs: boolean}) => void;
}

interface State {
  orientation: PrintOrientation;
  paperSize: PaperSize;
  shouldFitToWidth: boolean;
  showLinkURLs: boolean;
}

const ENTRY_API_ID_MATCH = /.*(etr_.+?)-.*/;

/**
 * Modal for configuring print options (orientation, fit-to-width) for ELN that is saved to user preferences
 */
class PrintOptionsModal extends React.Component<PrintOptionsModalProps, State> {
  static propTypes = {
    onRequestClose: PropTypes.func.isRequired,
    onRequestPrint: PropTypes.func.isRequired,
  };

  selectRef = React.createRef<HTMLSelectElement>();

  state: State = {
    orientation: preferences.get(PrintPreferences.ELN_ORIENTATION),
    paperSize: preferences.get(PrintPreferences.ELN_PAPER_SIZE),
    shouldFitToWidth: preferences.get(PrintPreferences.ELN_FIT_TO_WIDTH),
    showLinkURLs: preferences.get(PrintPreferences.ELN_SHOW_LINK_URLS),
  };

  componentDidMount(): void {
    // Make Esc work immediately after render
    if (this.selectRef.current) {
      this.selectRef.current.focus();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSubmit = () => {
    // Save options to be used for future prints
    preferences.set({
      [PrintPreferences.ELN_ORIENTATION]: this.state.orientation,
      [PrintPreferences.ELN_PAPER_SIZE]: this.state.paperSize,
      [PrintPreferences.ELN_FIT_TO_WIDTH]: this.state.shouldFitToWidth,
      [PrintPreferences.ELN_SHOW_LINK_URLS]: this.state.showLinkURLs,
    });

    this.props.onRequestPrint({
      dimensions: getPageDims(this.state.paperSize, this.state.orientation),
      shouldFitToWidth: this.state.shouldFitToWidth,
      showLinkURLs: this.state.showLinkURLs,
    });
  };

  _handleSaveHTML = (e: BasicEvent): void => {
    e.preventDefault();
    const entryApiIdMatch = ENTRY_API_ID_MATCH.exec(document.URL);
    const reactMountElement = document.querySelector('.react-mount');
    if (entryApiIdMatch && reactMountElement) {
      const entryApiId = entryApiIdMatch[1];
      const html = reactMountElement.innerHTML;
      const printerHtmlFile = new File([html], `${entryApiId}-printer-view.html`, {type: 'text/html'});
      ImportUtil.upload(printerHtmlFile).then((printerHtmlFileURL) => {
        if (printerHtmlFileURL) {
          notifications.success(`Saved printer view to ${printerHtmlFileURL}`);
          // eslint-disable-next-line no-console
          console.log(`Saved printer view to ${printerHtmlFileURL}`);
        }
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handlePaperSizeChange = (e: SelectChangeEvent) => {
    this.setState({paperSize: e.target.value as PaperSize});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOrientationChange = (e: InputChangeEvent) => {
    this.setState({orientation: e.target.value as PrintOrientation});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleFitChange = (e: InputChangeEvent) => {
    this.setState({shouldFitToWidth: e.target.checked});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleToggleShowLinkURLs = (e: InputChangeEvent) => {
    this.setState({showLinkURLs: e.target.checked});
  };

  render(): JSX.Element {
    const buttons: Array<ModalButton> = [{text: 'Print', submit: true, style: ButtonStyle.PRIMARY}];
    const isEntryURL = ENTRY_API_ID_MATCH.test(document.URL);

    if (BenchlingConfig.ELN_ENABLE_PRINT_PREVIEW_SNAPSHOTS && isEntryURL) {
      buttons.unshift({text: 'Save HTML', onClick: this._handleSaveHTML, style: ButtonStyle.SECONDARY});
    }
    return (
      <Modal
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handleSubmit}
        title="Print Settings"
        titleIcon="format-print"
        medium={true}
        buttons={buttons}
      >
        <div className="u-spacing-bottom">
          <Label>Paper Size</Label>
          <select
            name="paperSize"
            value={this.state.paperSize}
            ref={this.selectRef}
            onChange={this._handlePaperSizeChange}
          >
            {ALL_PAPER_SIZES.map((paperSize) => (
              <option key={paperSize} value={paperSize}>
                {PAPER_SIZE_INFO[paperSize].name}
              </option>
            ))}
          </select>
        </div>
        <hr />
        <div>
          <Label>Layout</Label>
          <div className="u-tiny-spacing-bottom">
            <Label className="checkbox-inline">
              <input
                name="shouldFitToWidth"
                type="checkbox"
                checked={this.state.shouldFitToWidth}
                onChange={this._handleFitChange}
              />
              Fit To Screen
            </Label>
          </div>
          <div>
            <Label className="radio-inline">
              <input
                name="orientation"
                type="radio"
                value={PrintOrientation.PORTRAIT}
                checked={this.state.orientation === PrintOrientation.PORTRAIT}
                onChange={this._handleOrientationChange}
              />
              Portrait
            </Label>
            <Label className="radio-inline">
              <input
                name="orientation"
                type="radio"
                value={PrintOrientation.LANDSCAPE}
                checked={this.state.orientation === PrintOrientation.LANDSCAPE}
                onChange={this._handleOrientationChange}
              />
              Landscape
            </Label>
            {/* Chrome correctly sets orientation based on @page rule; Firefox and Safari do not. */}
            {this.state.orientation === PrintOrientation.LANDSCAPE && !benchlingUtil.chrome && (
              <div className="u-small-spacing-top">
                <BenchlingIcon icon="warning" className="u-warning-color u-tiny-spacing-right" />
                On the next screen, configure your printer for <strong>landscape</strong>.
              </div>
            )}
          </div>
        </div>
        <hr />
        <div>
          <Label>Customization</Label>
          <div className="u-tiny-spacing-bottom">
            <Label className="checkbox-inline">
              <input
                name="showLinkURLs"
                type="checkbox"
                checked={this.state.showLinkURLs}
                onChange={this._handleToggleShowLinkURLs}
              />
              Show link URLs
            </Label>
          </div>
        </div>
      </Modal>
    );
  }
}

export default PrintOptionsModal;
